import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api, createDocument, updateDocument, getCollection, getDocument } from "../config";
import { buildLocationUrl } from "../../utils/locationUrl";
import _ from "lodash";

// Centralized Algolia filter builder for the brand-settings locations table.
// Statuses must be combined with OR; brand and accounts are ANDed alongside.
// Per-user access scoping (parent, invited, corporate, restricted invitee) is
// fully enforced by the secured Algolia API key; no UI-side filtering.
const buildLocationAlgoliaFilters = ({ brandName, statuses = [], accounts = [] }) => {
    const parts = [`brand:"${brandName}"`];
    const cleanStatuses = (statuses || []).filter(Boolean);
    if (cleanStatuses.length) {
        parts.push(`(${cleanStatuses.map((s) => `status:"${s}"`).join(" OR ")})`);
    }
    (accounts || []).filter(Boolean).forEach((account) => {
        parts.push(`account_email:"${account}"`);
    });
    return parts.join(" AND ");
};

export const locationFields = [
    {sortable:true,label:'Location',key:'location'},
    {sortable:true,label:'Address 1',key:'address1'},
    {sortable:true,label:'Address 2',key:'address2'},
    {sortable:true,label:'City',key:'city'},
    {sortable:true,label:'State',key:'state'},
    {sortable:true,label:'Zip',key:'zip'}
];


export const newLocation = createAsyncThunk(
    "brand/newLocation",
    async (location, { getState, rejectWithValue }) => {
        try{
            const {account,brand} = getState();

            const contacts = location.contacts || [];
            let data = {
                ...location,
                brandID:brand.brand.id
            };
            delete data.contacts;

            const assignedUserID = location.accountID || account.user.uid;
            data.users = {[assignedUserID]:true};

            if (!data.status) data.status = 'Open';

            let rs = await createDocument({entity:'locations'},data);
            if (contacts.length) {
                const result = await api.locationContactsUpdate({locationID: rs.id, contacts});
                rs.contacts = result?.data?.data?.contacts || [];
            } else {
                rs.contacts = [];
            }

            return rs;
        }catch(e){
            return rejectWithValue(e.message);
        }
    }
);
    

const exportLocationFields = [
    {label:'Location',key:'location'},
    {label:'Address 1',key:'address1'},
    {label:'Address 2',key:'address2'},
    {label:'City',key:'city'},
    {label:'State',key:'state'},
    {label:'Zip',key:'zip'},
    {label:'Account',key:'account_email'},
    {label:'Status',key:'status'},
    {label:'URL',key:'url'},
];

export const exportLocations = createAsyncThunk(
    "brand/exportLocations",
    async (_, { getState, rejectWithValue }) => {
        try{
            const {brand, account} = getState();
            const siteUrl = brand.brand?.siteUrl;
            const brandName = brand.brand?.brand;
            const includeBrandInLocationUrl = account.user?.includeBrandInLocationUrl;

            let tableHeaders = exportLocationFields.map(a=>a.label);
            let tableFields = exportLocationFields.map(a=>a.key);

            let tableValues = brand.locations.map(item => {
                const row = { ...item, url: buildLocationUrl(siteUrl, brandName, item, includeBrandInLocationUrl) || '' };
                return tableFields.map(field=>{
                    return '"' + (row[field]?row[field].toString().replace(/[^\x00-\x7F]/g, '').replace('#',''):'') + '"';
                });
            });

            let csvContent = "data:text/csv;charset=utf-8,";
            csvContent += tableHeaders.join(',') + '\n';
            csvContent += tableValues.map(row=>row.map(cell=>cell).join(',')).join('\n');

            const data = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", data);
            link.setAttribute("download", "export.csv");
            link.click();
        }catch(e){
            return rejectWithValue(e.message);
        }
    }
);

export const loadBrand = createAsyncThunk(
    "brand/loadBrand",
    async (id, { getState, rejectWithValue }) => {
        try{

            const {account} = getState();

            let brand = await getDocument('brands',id);

            // Invited users (parentUserID present) scope to the parent account so they
            // see the parent's locations on the standalone Locations page; restricted
            // invited users get further narrowed via Algolia in the table itself.
            const actorAccountID = account.user?.parentUserID || account.user?.uid;
            const locationWhere = (account.user?.corporate && !account.user?.parentUserID)
                ? [['brandID','==',id]]
                : [['brandID','==',id],['users.'+actorAccountID,'==',true]];
            let rsLocations = await getCollection('locations',{
                where: locationWhere
            });

            let locations = [];

            for (let i = 0; i < rsLocations.length; i++) {
                let location = rsLocations[i];
                if(
                    !location.deleted
                    &&
                    ![
                        "Non Participating",
                        "Temporarily Closed",
                        "Scheduled",
                        "Referral"
                    ].includes(location.status)
                ) locations.push({...location});
            }

            return {
                ...brand,
                locations
            };
        }catch(e){
            return rejectWithValue(e.message);
        }
    }
);
export const getLocationsFromAlgolia = createAsyncThunk(
    "brand/getLocationsFromAlgolia",
    async (payload = {}, { getState, rejectWithValue }) => {
        try {
            const { brand, pager, algolia } = getState();
            if (!algolia.index || !brand.brand?.brand) return rejectWithValue("Algolia index not ready");

            const text = typeof payload === 'string' ? payload : (payload?.text ?? brand.locationFilter ?? "");
            const statuses = payload?.statuses ?? brand.locationStatusFilters ?? [];
            const accounts = payload?.accounts ?? brand.locationAccountFilters ?? [];
            const filters = buildLocationAlgoliaFilters({ brandName: brand.brand.brand, statuses, accounts });

            const response = await algolia.index.search(text, {
                maxValuesPerFacet: 2000,
                filters,
                hitsPerPage: Number(payload?.limit ?? brand.limit),
                page: pager.page,
                facets: ["status", "account_email"],
                sortFacetValuesBy: "alpha",
            });

            return {
                filter: text,
                statuses,
                accounts,
                limit: Number(payload?.limit ?? brand.limit),
                pages: Math.ceil(response.nbHits / Number(payload?.limit ?? brand.limit)),
                total: response.nbHits,
                locations: response.hits.map((h) => ({ id: h.objectID, ...h })),
                locationStatuses: response.facets?.status ? Object.keys(response.facets.status) : [],
                locationAccounts: response.facets?.account_email ? Object.keys(response.facets.account_email) : [],
            };
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const filterLocations = createAsyncThunk(
    "brand/filterLocations",
    async (payload, { getState, rejectWithValue }) => {
        try{
            const {brand,pager,algolia} = getState();
            if(algolia.index) {
                const text = typeof payload === 'string' ? payload : (payload?.text ?? brand.locationFilter ?? "");
                const statuses = payload?.statuses ?? brand.locationStatusFilters ?? [];
                const accounts = payload?.accounts ?? brand.locationAccountFilters ?? [];
                const filters = buildLocationAlgoliaFilters({ brandName: brand.brand.brand, statuses, accounts });
                const response = await algolia.index.search(text, {
                    maxValuesPerFacet: 2000,
                    filters,
                    hitsPerPage: Number(brand.limit),
                    page: pager.page,
                    facets: ["status", "account_email"],
                    sortFacetValuesBy: "alpha",
                });
                return {
                    pages: Math.ceil(response.nbHits/brand.limit),
                    total: response.nbHits,
                    filter:text,
                    statuses,
                    accounts,
                    locationStatuses: response.facets?.status ? Object.keys(response.facets.status) : [],
                    locationAccounts: response.facets?.account_email ? Object.keys(response.facets.account_email) : [],
                    locations: response.hits.map((h) => ({ id: h.objectID, ...h }))
                };
            }
            return rejectWithValue("Algolia index required for brand locations table");
        }catch(e){
            return rejectWithValue(e.message);
        }
    }
);
export const setLimit = createAsyncThunk(
    "brand/setLimit",
    async (limit, { getState, rejectWithValue }) => {
        try{
            const {brand,algolia} = getState();
            if(algolia.index) {
                const filters = buildLocationAlgoliaFilters({
                    brandName: brand.brand.brand,
                    statuses: brand.locationStatusFilters,
                    accounts: brand.locationAccountFilters,
                });
                const response = await algolia.index.search(brand.locationFilter || "", {
                    maxValuesPerFacet: 2000,
                    filters,
                    hitsPerPage: Number(limit),
                    page: 0,
                    facets: ["status", "account_email"],
                    sortFacetValuesBy: "alpha",
                });
                return {
                    pages: Math.ceil(response.nbHits/limit),
                    limit:Number(limit),
                    total: response.nbHits,
                    statuses: brand.locationStatusFilters || [],
                    accounts: brand.locationAccountFilters || [],
                    locationStatuses: response.facets?.status ? Object.keys(response.facets.status) : [],
                    locationAccounts: response.facets?.account_email ? Object.keys(response.facets.account_email) : [],
                    locations: response.hits.map((h) => ({ id: h.objectID, ...h }))
                };
            }
            return rejectWithValue("Algolia index required for brand locations table");
        }catch(e){
            return rejectWithValue(e.message);
        }
    }
);
export const sortLocations = createAsyncThunk(
    "brand/sortLocations",
    async (sort, { getState, rejectWithValue }) => {
        try{
            return rejectWithValue("Algolia-only sorting is handled via index replicas for brand locations table");
        }catch(e){
            return rejectWithValue(e.message);
        }
    }
);

export const removeLocation = createAsyncThunk(
    "brand/removeLocation",
    async (id, { getState, rejectWithValue }) => {
        try{
            const { account, brand } = getState();
            const location = (brand.brand?.locations || []).find(l => l.id === id) || (brand.locations || []).find(l => l.id === id);
            const actorAccountID = account.user?.parentUserID || account.user?.uid;
            const ownsLocation = !!(location?.users && actorAccountID && location.users[actorAccountID]);
            if(account.user?.corporate && !ownsLocation && location && location.status !== 'Referral') {
                return rejectWithValue('Corporate users may only close unowned locations when the location is currently in Referral status.');
            }
            await updateDocument({entity:'locations',id:id},{deleted:true});
            return id;
        }catch(e){
            return rejectWithValue(e.message);
        }
    }
);

export const loadLocationForEdit = createAsyncThunk(
    "brand/loadLocationForEdit",
    async (location, { rejectWithValue }) => {
        try {
            const [fullLocation, contactResult] = await Promise.all([
                getDocument('locations', location.id),
                api.locationContactsList({locationID: location.id}),
            ]);
            return {
                ...fullLocation,
                contacts: contactResult?.data?.data?.contacts || [],
            };
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const updateLocationContacts = createAsyncThunk(
    "brand/updateLocationContacts",
    async (contacts, { getState, rejectWithValue }) => {
        try{
            const {brand} = getState();
            const result = await api.locationContactsUpdate({
                locationID: brand.selectedLocation.id,
                contacts,
            });
            return result?.data?.data?.contacts || [];
        }catch(e){
            return rejectWithValue(e.message);
        }
    }
);

export const brandSlice = createSlice({
    name: "brand",
    initialState: {
        loading:true,
        loadingError:null,
        sorting:false,
        sortingError:null,
        loadingLocations:false,
        exporting:false,
        exportingError:null,
        brand:null,
        locations:[],
        total:0,
        locationOrder:'location',
        locationOrderBy:'ascending',
        locationFilter:'',
        locationStatusFilters:[],
        locationAccountFilters:[],
        locationStatuses:[],
        locationAccounts:[],
        pages:0,
        limit:50,
        activeModal:null,
        adding:false,
        addingError:null,
        selectedLocation:null,
        loadingLocationForEdit:false,
        loadingLocationForEditError:null,
        updatingContactsError:null
    },
    reducers: {
        setActiveModal: (state,action) => {
            state.activeModal = action.payload;
        },
        setSelectedLocation: (state,action) => {
            state.selectedLocation = action.payload;
        },
        updateLocation: (state,action) => {
            state.brand.locations = state.brand.locations.map(l=>l.id===action.payload.id?action.payload:l);
            state.locations = state.locations.map(l=>l.id===action.payload.id?action.payload:l);
            state.activeModal = null;
            state.selectedLocation = null;
        },
        setLocationSort: (state, action) => {
            state.locationOrder = action.payload.order;
            state.locationOrderBy = action.payload.orderBy;
        }
    },
    extraReducers: {
        [loadBrand.fulfilled]: (state, action) => {
            state.brand = action.payload;
            state.pages = 0;
            state.locationOrder = 'location';
            state.locationOrderBy = 'ascending';
            state.locations = [];
            state.loading = false;
            return;
        },
        [loadBrand.pending]: (state, action) => {
            state.loading = true;
            return;
        },
        [loadBrand.rejected]: (state, action) => {
            state.loadingError = action.payload;
            state.loading = false;
            return;
        },
        [getLocationsFromAlgolia.fulfilled]: (state, action) => {
            if (action.payload.locations.length === 0 && !action.payload.filter && !action.payload.statuses.length && !action.payload.accounts.length) { state.loadingLocations = false; return; }
            state.locations = action.payload.locations;
            state.pages = action.payload.pages;
            state.total = action.payload.total;
            state.limit = action.payload.limit;
            state.locationFilter = action.payload.filter;
            state.locationStatusFilters = action.payload.statuses;
            state.locationAccountFilters = action.payload.accounts;
            state.locationStatuses = action.payload.locationStatuses;
            state.locationAccounts = action.payload.locationAccounts;
            state.loadingLocations = false;
            return;
        },
        [getLocationsFromAlgolia.pending]: (state) => {
            state.loadingLocations = true;
            return;
        },
        [getLocationsFromAlgolia.rejected]: (state, action) => {
            state.loadingLocations = false;
            state.sortingError = action.payload;
            return;
        },
        [sortLocations.fulfilled]: (state, action) => {
            state.locationOrder = action.payload.locationOrder;
            state.locationOrderBy = action.payload.locationOrderBy;
            state.locations = action.payload.locations;
            state.sorting = false;
            return;
        },
        [sortLocations.pending]: (state, action) => {
            state.sorting = true;
            return;
        },
        [sortLocations.rejected]: (state, action) => {
            state.sortingError = action.payload;
            state.sorting = false;
            return;
        },
        [removeLocation.fulfilled]: (state, action) => {
            state.brand.locations = state.brand.locations.filter(l=>l.id!==action.payload);
            state.locations = state.locations.filter(l=>l.id!==action.payload);
            state.removing = false;
            return;
        },
        [removeLocation.pending]: (state, action) => {
            state.removing = true;
            return;
        },
        [removeLocation.rejected]: (state, action) => {
            state.sortingError = action.payload;
            state.removing = false;
            return;
        },
        [setLimit.fulfilled]: (state, action) => {
            state.limit = action.payload.limit;
            state.pages = action.payload.pages;
            state.locations = action.payload.locations;
            if(action.payload.total !== undefined) state.total = action.payload.total;
            if(action.payload.statuses) state.locationStatusFilters = action.payload.statuses;
            if(action.payload.accounts) state.locationAccountFilters = action.payload.accounts;
            if(action.payload.locationStatuses) state.locationStatuses = action.payload.locationStatuses;
            if(action.payload.locationAccounts) state.locationAccounts = action.payload.locationAccounts;
            return;
        },
        [filterLocations.fulfilled]: (state, action) => {
            state.pages = action.payload.pages;
            state.locationFilter = action.payload.filter;
            state.locations = action.payload.locations;
            if(action.payload.total !== undefined) state.total = action.payload.total;
            if(action.payload.statuses) state.locationStatusFilters = action.payload.statuses;
            if(action.payload.accounts) state.locationAccountFilters = action.payload.accounts;
            if(action.payload.locationStatuses) state.locationStatuses = action.payload.locationStatuses;
            if(action.payload.locationAccounts) state.locationAccounts = action.payload.locationAccounts;
            return;
        },
        [exportLocations.fulfilled]: (state, action) => {
            state.exporting = false;
            return;
        },
        [exportLocations.pending]: (state, action) => {
            state.exportingError = null;
            state.exporting = true;
            return;
        },
        [exportLocations.rejected]: (state, action) => {
            state.exportingError = action.payload;
            state.exporting = false;
            return;
        },
        [newLocation.fulfilled]: (state, action) => {
            state.brand.locations.unshift({...action.payload});
            state.locations.unshift({...action.payload});
            state.adding = false;
            return;
        },
        [newLocation.pending]: (state, action) => {
            state.addingError = null;
            state.adding = true;
            return;
        },
        [newLocation.rejected]: (state, action) => {
            state.addingError = action.payload;
            state.adding = false;
            return;
        },
        [loadLocationForEdit.fulfilled]: (state, action) => {
            state.selectedLocation = action.payload;
            state.loadingLocationForEdit = false;
            state.loadingLocationForEditError = null;
            return;
        },
        [loadLocationForEdit.pending]: (state, action) => {
            // Open immediately from the Algolia row, then hydrate the complete
            // location and canonical contacts behind a visible loading state.
            // This keeps a zero-min-instance callable from making the click
            // appear unresponsive during a cold start.
            state.selectedLocation = action.meta.arg;
            state.activeModal = "editLocation";
            state.loadingLocationForEdit = true;
            state.loadingLocationForEditError = null;
            return;
        },
        [loadLocationForEdit.rejected]: (state, action) => {
            state.loadingLocationForEdit = false;
            state.loadingLocationForEditError = action.payload
                || action.error?.message
                || "Please try again.";
            return;
        },
        [updateLocationContacts.fulfilled]: (state, action) => {
            state.brand.locations = state.brand.locations.map(l=>l.id===state.selectedLocation.id?Object.assign({...l},{contacts:action.payload}):l);
            state.locations = state.locations.map(l=>l.id===state.selectedLocation.id?Object.assign({...l},{contacts:action.payload}):l);
            return;
        },
        [updateLocationContacts.pending]: (state, action) => {
            state.updatingContactsError = null;
            return;
        },
        [updateLocationContacts.rejected]: (state, action) => {
            state.updatingContactsError = action.payload;
            return;
        }
    },
});

export const {
    setActiveModal,
    setSelectedLocation,
    updateLocation,
    setLocationSort
} = brandSlice.actions;
export default brandSlice.reducer;
